/** *
 * Interface (shared by every provider):
 *
 *   MapClient
 *     load(): Promise<MapClient>
 *     createMap(container, { center?, zoom? }): MapHandle
 *
 *   MapHandle
 *     addIconMarker({ position, iconUrl, size?, zIndex? }): IconMarker
 *     addCaptainMarker({ position, iconUrl, size? }): CaptainMarker
 *     fitBounds(positions[], { maxZoom?, padding? }): void
 *     setCenter(position): void
 *     setZoom(zoom): void
 *     getZoom(): number
 *     panTo(position): void
 *
 *   CaptainMarker         IconMarker
 *     getPosition()         setIcon(iconUrl)
 *     setPosition(pos)      remove()
 *     setIcon(iconUrl)
 *     setRotation(deg)
 *     remove()
 *
 * Positions crossing the boundary are always plain { lat, lng }.
 */

import { Loader } from "@googlemaps/js-api-loader";

export default class GoogleMapClient {
  constructor() {
    this.google = null;
    this._loadPromise = null;
  }

  load() {
    if (!this._loadPromise) {
      const loader = new Loader({
        apiKey: process.env.VUE_APP_GOOGLE_MAP_API_KEY,
        version: "weekly",
        libraries: ["drawing", "geometry"],
      });
      this._loadPromise = loader.load().then((google) => {
        this.google = google;
        this._buildOverlayClass();
        return this;
      });
    }
    return this._loadPromise;
  }

  createMap(container, { center, zoom } = {}) {
    const el =
      typeof container === "string"
        ? document.getElementById(container)
        : container;

    const map = new this.google.maps.Map(el, {
      ...(center ? { center } : {}),
      ...(typeof zoom === "number" ? { zoom } : {}),
      zoomControl: false,
      streetViewControl: false,
      mapTypeControl: false,
      clickableIcons: false,
    });

    return new GoogleMapHandle(this.google, map, this.CaptainOverlay);
  }

  // Built once the SDK is available; needs google.maps.OverlayView at define time.
  _buildOverlayClass() {
    const google = this.google;

    this.CaptainOverlay = class extends google.maps.OverlayView {
      constructor(position, map, iconUrl, size) {
        super();
        this.position = position; // google.maps.LatLng
        this.iconUrl = iconUrl;
        this.size = size || 52;
        this.div = null;
        this.img = null;
        this.setMap(map);
      }

      setIcon(iconUrl) {
        if (this.img) this.img.src = iconUrl;
      }

      onAdd() {
        this.div = document.createElement("div");
        this.div.style.position = "absolute";

        const img = document.createElement("img");
        img.src = this.iconUrl;
        img.style.width = `${this.size}px`;
        img.style.height = `${this.size}px`;
        img.style.objectFit = "contain";
        img.style.transformOrigin = "center";
        img.style.willChange = "transform";

        this.img = img;
        this.div.appendChild(img);

        this.getPanes().overlayMouseTarget.appendChild(this.div);
      }

      draw() {
        const projection = this.getProjection();
        if (!projection || !this.div) return;
        const point = projection.fromLatLngToDivPixel(this.position);
        if (point) {
          const half = this.size / 2;
          this.div.style.left = point.x - half + "px";
          this.div.style.top = point.y - half + "px";
        }
      }

      updatePosition(latLng) {
        this.position = latLng;
        this.draw();
      }

      rotate(deg) {
        if (this.img) this.img.style.transform = `rotate(${deg}deg)`;
      }

      onRemove() {
        if (this.div && this.div.parentNode) {
          this.div.parentNode.removeChild(this.div);
        }
        this.div = null;
      }
    };
  }
}

class GoogleMapHandle {
  constructor(google, map, CaptainOverlay) {
    this.google = google;
    this.map = map;
    this.CaptainOverlay = CaptainOverlay;
  }

  addIconMarker({ position, iconUrl, size = 40, zIndex }) {
    const marker = new this.google.maps.Marker({
      position: { lat: position.lat, lng: position.lng },
      map: this.map,
      ...(typeof zIndex === "number" ? { zIndex } : {}),
    });
    const iconMarker = new GoogleIconMarker(this.google, marker, size);
    iconMarker.setIcon(iconUrl);
    return iconMarker;
  }

  addCaptainMarker({ position, iconUrl, size = 52 }) {
    const overlay = new this.CaptainOverlay(
      new this.google.maps.LatLng(position.lat, position.lng),
      this.map,
      iconUrl,
      size
    );
    return new GoogleCaptainMarker(this.google, overlay);
  }

  fitBounds(positions, { maxZoom, padding } = {}) {
    const bounds = new this.google.maps.LatLngBounds();
    (positions || []).forEach((p) =>
      bounds.extend({ lat: p.lat, lng: p.lng })
    );
    if (bounds.isEmpty()) return;

    this.map.fitBounds(bounds, padding);

    if (typeof maxZoom === "number") {
      const listener = this.google.maps.event.addListener(
        this.map,
        "idle",
        () => {
          if (this.map.getZoom() > maxZoom) this.map.setZoom(maxZoom);
          this.google.maps.event.removeListener(listener);
        }
      );
    }
  }

  setCenter(position) {
    this.map.setCenter(position);
  }

  setZoom(zoom) {
    this.map.setZoom(zoom);
  }

  getZoom() {
    return this.map.getZoom();
  }

  panTo(position) {
    this.map.panTo(new this.google.maps.LatLng(position.lat, position.lng));
  }
}

class GoogleIconMarker {
  constructor(google, marker, size) {
    this.google = google;
    this.marker = marker;
    this.size = size;
  }

  setIcon(iconUrl) {
    this.iconUrl = iconUrl;
    this._applyIcon(iconUrl, this.size, this.size);

    // Google Markers stretch the image to scaledSize, so once the real
    // dimensions are known, fit non-square icons inside the size box.
    if (typeof Image === "undefined") return;
    const img = new Image();
    img.onload = () => {
      if (this.iconUrl !== iconUrl) return;
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      if (!w || !h || w === h) return;
      const scale = this.size / Math.max(w, h);
      this._applyIcon(iconUrl, Math.round(w * scale), Math.round(h * scale));
    };
    img.src = iconUrl;
  }

  _applyIcon(url, width, height) {
    this.marker.setIcon({
      url,
      scaledSize: new this.google.maps.Size(width, height),
      origin: new this.google.maps.Point(0, 0),
      anchor: new this.google.maps.Point(width / 2, height / 2),
    });
  }

  remove() {
    this.marker.setMap(null);
  }
}

class GoogleCaptainMarker {
  constructor(google, overlay) {
    this.google = google;
    this.overlay = overlay;
  }

  getPosition() {
    const p = this.overlay.position;
    if (!p) return null;
    return { lat: p.lat(), lng: p.lng() };
  }

  setPosition(position) {
    this.overlay.updatePosition(
      new this.google.maps.LatLng(position.lat, position.lng)
    );
  }

  setIcon(iconUrl) {
    this.overlay.setIcon(iconUrl);
  }

  setRotation(deg) {
    this.overlay.rotate(deg);
  }

  remove() {
    this.overlay.setMap(null);
  }
}
